import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, ArrowRight, AlertTriangle } from "lucide-react";
import Reveal from "../components/Reveal";

const steps = [
  {
    n: 1, title: "Schedule Your Free Consultation", tag: "Step 1", time: "5 minutes",
    body: "Share details about your situation in a secure, judgment-free environment. We ask about the scam type, amounts involved, payment methods used, and timeline—because each of these factors significantly affects your recovery options.",
    points: ["Identify which scam category applies (crypto, romance, investment, etc.)", "Document when payments occurred—timing is critical for chargebacks", "Record payment methods used—credit cards offer more protection than wire transfers"],
    why: "Many victims don't realize that acting within specific time windows (often 60-120 days for chargebacks) can make the difference between recovery and permanent loss.",
  },
  {
    n: 2, title: "Review Your Personalized Analysis", tag: "Step 2", time: "Instant",
    body: "Receive an immediate, detailed breakdown of your situation. We explain exactly why your experience qualifies as fraud, which recovery paths are realistic for your specific case, and what evidence you should preserve right now.",
    points: ["Clear explanation of the scam tactics used against you", "Honest probability evaluation based on payment method and timing", "Specific evidence checklist tailored to your situation"],
    why: "Understanding why you were targeted helps you recognize future attempts. Scammers often use sophisticated psychological tactics that work on intelligent, successful people.",
  },
  {
    n: 3, title: "Understand Your Realistic Options", tag: "Step 3", time: "Take your time",
    body: "Learn which recovery paths genuinely apply to your case. We'll be honest: some methods work better than others depending on how you paid. Credit card disputes have higher success rates than cryptocurrency recovery, for example.",
    points: ["Law enforcement reporting—what it can and cannot accomplish", "Financial institution disputes—timelines and success factors", "Legal options—when they're worth pursuing and when they're not"],
    why: "The recovery industry is filled with services that overpromise. We tell you upfront which paths are worth your time and which are likely dead ends.",
  },
  {
    n: 4, title: "Decide Your Next Steps", tag: "Step 4", time: "Your decision",
    body: "Armed with accurate information, choose your path forward. Some victims successfully recover funds on their own using our guidance. Others want professional assistance. Some focus on emotional recovery and prevention. All choices are valid.",
    points: ["Self-help resources for independent recovery attempts", "When professional assistance genuinely adds value", "Emotional support and prevention resources"],
    why: "You're in control. We never pressure you toward paid services. Many victims find that understanding what happened is itself therapeutic, even without financial recovery.",
  },
];

const myths = [
  { myth: "Recovery companies can guarantee getting your money back", reality: "No legitimate service can guarantee recovery. Anyone who promises 100% success is either lying or running a recovery scam themselves. Success depends on payment method (credit cards are most recoverable), timing (faster is better), evidence quality, and jurisdiction where scammers operate.", tip: "Ask any recovery service for their documented success rate by payment type. If they can't provide specifics, that's a red flag." },
  { myth: "Cryptocurrency and wire transfers are impossible to recover", reality: "This is outdated thinking. While harder than credit card chargebacks, crypto and wire transfers can be recovered in specific circumstances. We've seen recovery on cases where victims acted quickly, had good documentation, and the funds moved through traceable exchanges or regulated financial institutions.", tip: "If you paid via crypto or wire, document every transaction hash and bank reference number immediately. This information is crucial for any recovery attempt." },
  { myth: "Recovery should happen within weeks", reality: "Legitimate recovery—especially involving legal action or law enforcement cooperation—typically takes 6-18 months. Bank disputes can be faster (60-90 days) but still require patience. Be extremely skeptical of anyone promising quick results.", tip: "Set realistic expectations: bank disputes take 1-3 months; legal recovery takes 6-18 months; law enforcement cases can take years." },
];

const controls = [
  { title: "After Consultation", desc: "You decide if you want to learn more or stop here. No pressure, no follow-up calls if you don't want them." },
  { title: "Before Any Action", desc: "You'll always understand the costs, risks, and realistic outcomes before committing to anything." },
  { title: "At Every Step", desc: "You can pause, ask questions, or walk away at any time. This is your journey, at your pace." },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <section className="hero-mesh section">
        <div className="container max-w-4xl text-center">
          <Reveal>
            <div className="trust-badge mx-auto mb-5">Our Process</div>
            <h1 className="mb-5 font-display text-4xl font-extrabold tracking-tight text-primary md:text-5xl">
              How Investment Scam Recovery Works
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              Our proven 4-step process helps you understand your situation, explore realistic recovery
              options, and take informed action—with complete transparency.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="container max-w-4xl space-y-8">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <div className="calm-card hover-lift" data-testid={`step-${s.n}`}>
                <div className="flex flex-col gap-5 md:flex-row">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary font-display text-2xl font-bold text-primary-foreground">
                    {s.n}
                  </div>
                  <div className="flex-1">
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                      <h3 className="font-display text-xl font-semibold md:text-2xl">{s.title}</h3>
                      <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">{s.tag}</span>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-secondary"><Clock className="h-3.5 w-3.5" /> {s.time}</span>
                    </div>
                    <p className="mb-4 text-muted-foreground">{s.body}</p>
                    <ul className="mb-4 space-y-2">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-sm text-foreground/80">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-safe" /> {p}
                        </li>
                      ))}
                    </ul>
                    <div className="rounded-lg border border-secondary/10 bg-calm-light/40 p-3">
                      <p className="text-sm text-muted-foreground"><strong className="text-secondary">Why this matters:</strong> {s.why}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section bg-muted/30">
        <div className="container max-w-4xl">
          <Reveal className="mb-10 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold">What We've Learned From Real Cases</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              These insights come from analyzing hundreds of fraud cases. Understanding these truths will
              help you make better decisions and avoid common pitfalls.
            </p>
          </Reveal>
          <div className="space-y-5">
            {myths.map((m, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="calm-card">
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-destructive/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-destructive">
                    <AlertTriangle className="h-3.5 w-3.5" /> Myth
                  </div>
                  <h3 className="mb-3 font-display text-lg font-semibold">{m.myth}</h3>
                  <p className="mb-3 text-sm text-muted-foreground"><strong className="text-safe">Reality:</strong> {m.reality}</p>
                  <div className="rounded-lg border border-accent/30 bg-accent/10 p-3">
                    <p className="text-sm text-muted-foreground"><strong className="text-accent-foreground">Actionable tip:</strong> {m.tip}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container max-w-5xl">
          <Reveal className="mb-10 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold">You're Always in Control</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">We never pressure you. Here are the clear decision points in our process.</p>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {controls.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="rounded-2xl bg-trust-light/60 p-8 hover-lift">
                  <h3 className="mb-2 font-semibold text-primary">{c.title}</h3>
                  <p className="text-sm text-muted-foreground">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-primary text-primary-foreground">
        <div className="container max-w-3xl text-center">
          <h2 className="mb-4 font-display text-3xl font-bold">Ready to Start?</h2>
          <p className="mx-auto mb-8 max-w-xl text-white/75">
            Reach out to our team for a private, no-pressure conversation about your situation.
          </p>
          <Link to="/contact" className="btn-teal" data-testid="how-cta-contact">
            Contact Us <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
